import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
    Plus, 
    Search, 
    Filter, 
    Eye, 
    EyeOff, 
    Edit2, 
    Trash2,
    Mountain,
    Cloud,
    Waves,
    Zap,
    AlertCircle,
    CheckCircle2,
    X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { autoTranslateExpedition } from '../../lib/translate';
import { sortExpeditions } from '../../lib/expeditionOrder';
import ExpeditionEditor from './ExpeditionEditor';

const ExpeditionsManager: React.FC = () => {
    const { theme, expeditions, setExpeditions } = useOutletContext<{ 
        theme: 'light' | 'dark';
        expeditions: any[];
        setExpeditions: React.Dispatch<React.SetStateAction<any[]>>;
    }>();
    
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<'all' | string>('all');
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingExp, setEditingExp] = useState<any>(null);
    const [notification, setNotification] = useState<{ message: string, type: 'error' | 'success' } | null>(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [translateProgress, setTranslateProgress] = useState<string>('');
    
    // Custom Dialog State
    const [customConfirm, setCustomConfirm] = useState<{
        message: string;
        onConfirm: () => void;
    } | null>(null);

    // Auto-hide notification
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    const handleNewExpedition = () => {
        setEditingExp(null);
        setIsEditorOpen(true);
    };

    const handleEditExpedition = (exp: any) => {
        setEditingExp(exp);
        setIsEditorOpen(true);
    };

    const handleToggleVisibility = async (id: string) => {
        const exp = expeditions.find(e => e.id === id);
        if (!exp) return;

        const nextVisible = exp.is_visible === false ? true : false;

        try {
            const { error } = await supabase
                .from('expeditions')
                .update({ is_visible: nextVisible })
                .eq('id', id);

            if (error) throw error;

            // Sync with local React state
            setExpeditions(prev => prev.map(e => e.id === id ? { ...e, is_visible: nextVisible } : e));
            setNotification({ 
                message: nextVisible ? 'Expedición ahora es Visible' : 'Expedición Ocultada', 
                type: 'success' 
            });
        } catch (err: any) {
            console.error('Error toggling visibility:', err);
            setNotification({ message: `Error al cambiar visibilidad: ${err.message || err}`, type: 'error' });
        }
    };

    const handleDeleteClick = (exp: any) => {
        setCustomConfirm({
            message: `¿Estás seguro/a de que deseas eliminar "${exp.name_es}"? Esta acción no se puede deshacer y se borrará permanentemente de Supabase.`,
            onConfirm: async () => {
                try {
                    const { error } = await supabase
                        .from('expeditions')
                        .delete()
                        .eq('id', exp.id);

                    if (error) throw error;

                    // Update global state
                    setExpeditions(prev => prev.filter(e => e.id !== exp.id));
                    setNotification({ message: 'Expedición eliminada correctamente', type: 'success' });
                } catch (err: any) {
                    console.error('Error deleting from Supabase:', err);
                    setNotification({ message: `Error al eliminar: ${err.message || err}`, type: 'error' });
                }
            }
        });
    };

    const handleSave = async (data: any) => {
        try {
            const image = data.gallery?.[0]?.src || '';
            const gallery = data.gallery || [];

            // Parse numeric values robustly to avoid Postgres casting errors
            const parsedPriceFrom = (data.price_from !== undefined && data.price_from !== null && data.price_from !== '') 
                ? (parseInt(String(data.price_from).replace(/\D/g, '')) || null) 
                : null;

            const parsedPriceFlat = (data.price_flat !== undefined && data.price_flat !== null && data.price_flat !== '')
                ? (parseFloat(String(data.price_flat).replace(/[^0-9.]/g, '')) || null)
                : null;

            // 1. Prepare base database payload with Spanish data
            const dbPayloadBase = {
                ...(editingExp || {}),
                id: data.id,
                slug: data.slug,
                category: data.category,
                // Spanish (source)
                name_es: data.name_es,
                desc_es: data.desc_es || '',
                catchphrase_es: data.catchphrase_es || null,
                best_season_es: data.best_season_es || null,
                includes_es: data.includes_es || [],
                excludes_es: data.excludes_es || [],
                recommendations_es: data.recommendations_es || [],
                policies_es: data.policies_es || [],
                itinerary_es: data.itinerary_es || [],
                // Non-translatable fields
                difficulty: data.difficulty || 'mid',
                duration: data.duration || '',
                distance: data.distance || null,
                price_from: parsedPriceFrom,
                image: image || '',
                gallery: gallery || [],
                gallery_layout: data.gallery_layout || 'landscape',
                pricing_tiers: (data.pricing_tiers && data.pricing_tiers.length > 0)
                    ? data.pricing_tiers 
                    : (parsedPriceFrom ? [{ min_people: 1, price_per_person: parsedPriceFrom }] : []),
                variants: data.variants || null,
                duration_type: data.duration_type || 'full_day',
                itinerary_type: data.itinerary_type || 'none',
                price_flat: parsedPriceFlat,
                // Ensure required NOT NULL columns for initial insert before async translation
                name_en: editingExp?.name_en || data.name_es,
                desc_en: editingExp?.desc_en || data.desc_es || '',
            };

            // 2. Perform the database write instantly to avoid UX blocking
            const { error: saveError } = await supabase
                .from('expeditions')
                .upsert(dbPayloadBase);

            if (saveError) throw saveError;

            // 3. Sync immediately with local React state to update the UI instantly
            setExpeditions(prev => {
                const index = prev.findIndex(e => e.id === data.id);
                if (index >= 0) {
                    const copy = [...prev];
                    copy[index] = { ...copy[index], ...dbPayloadBase };
                    return sortExpeditions(copy);
                } else {
                    return sortExpeditions([...prev, dbPayloadBase as any]);
                }
            });

            // 4. Close editor modal and alert the admin that it succeeded and translations started in background
            setIsEditorOpen(false);
            setNotification({ message: 'Expedición guardada (Traduciendo en segundo plano...)', type: 'success' });

            // 5. Run translation asynchronously in the background to avoid blocking the client
            (async () => {
                try {
                    const translated = await autoTranslateExpedition(data);
                    
                    // Save the translations to Supabase
                    const { error: translateError } = await supabase
                        .from('expeditions')
                        .update(translated)
                        .eq('id', data.id);

                    if (translateError) throw translateError;

                    // Sync the new translations into local React state
                    setExpeditions(prev => 
                        prev.map(e => e.id === data.id ? { ...e, ...translated } : e)
                    );

                    setNotification({ message: 'Traducciones completadas e integradas con éxito', type: 'success' });
                } catch (tErr) {
                    console.error('Error in background translation:', tErr);
                }
            })();

        } catch (err: any) {
            setIsTranslating(false);
            setTranslateProgress('');
            console.error('Error saving to Supabase:', err);
            setNotification({ message: `Error al guardar: ${err.message || err}`, type: 'error' });
            throw err;
        }
    };
    
    const categories = [
        { id: 'trekking', icon: Mountain, label: 'Trekking' },
        { id: 'climbing', icon: Zap, label: 'Escalada' },
        { id: 'water', icon: Waves, label: 'Agua' },
        { id: 'wellness', icon: Cloud, label: 'Wellness' },
    ];

    const filteredExpeditions = useMemo(() => {
        return sortExpeditions(expeditions || [])
            .filter(e => selectedCategory === 'all' || e.category === selectedCategory)
            .filter(e => e.name_es.toLowerCase().includes(search.toLowerCase()));
    }, [expeditions, selectedCategory, search]);

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Actions */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className={`text-4xl font-title uppercase tracking-tighter ${isDark ? 'text-white' : 'text-gray-900'}`}>Gestor de Expediciones</h1>
                    <p className="text-gray-400 text-[10px] uppercase tracking-[0.3em] font-bold mt-1">Administra tu catálogo de rutas y experiencias.</p>
                </div>
                
                <button 
                    onClick={handleNewExpedition}
                    className="bg-[#00d4ff] text-black px-8 py-4 font-title text-lg uppercase tracking-widest flex items-center gap-3 hover:bg-white transition-all shadow-[0_10px_30px_-10px_rgba(0,212,255,0.4)] rounded-[10px]"
                >
                    <Plus className="w-5 h-5" />
                    Nueva Expedición
                </button>
            </div>

            {/* Filters Bar */}
            <div className={`flex flex-col lg:flex-row gap-4 items-center justify-between border p-4 rounded-[10px] transition-colors ${
                isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-sm'
            }`}>
                <div className="flex items-center gap-2 overflow-x-auto w-full lg:w-auto scrollbar-hide">
                    <button 
                        onClick={() => setSelectedCategory('all')}
                        className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap rounded-[10px] transition-all ${
                            selectedCategory === 'all'
                            ? 'bg-[#00d4ff] text-black'
                            : isDark ? 'bg-black border border-rock text-gray-400 hover:border-[#00d4ff] hover:text-white' : 'bg-gray-50 border border-gray-200 text-gray-500 hover:border-[#00d4ff] hover:text-gray-900'
                        }`}
                    >
                        Todas
                    </button>
                    {categories.map((cat) => (
                        <button 
                            key={cat.id} 
                            onClick={() => setSelectedCategory(cat.id)}
                            className={`px-4 py-2 border text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all flex items-center gap-2 rounded-[10px] ${
                                selectedCategory === cat.id
                                ? 'bg-[#00d4ff] text-black border-[#00d4ff]'
                                : isDark ? 'bg-black border-rock text-gray-400 hover:border-[#00d4ff] hover:text-white' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-[#00d4ff] hover:text-gray-900'
                            }`}
                        >
                            <cat.icon className="w-3.5 h-3.5" />
                            {cat.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-4 w-full lg:w-auto">
                    <div className="relative flex-1 lg:w-64">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input 
                            type="text" 
                            placeholder="Buscar expedición..." 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={`w-full border pl-11 pr-4 py-3 text-xs outline-none focus:border-[#00d4ff] transition-all rounded-[10px] ${
                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                            }`}
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {filteredExpeditions.map((exp) => {
                    const isHidden = exp.is_visible === false;
                    return (
                        <div key={exp.id} className={`border group transition-all duration-300 rounded-[10px] overflow-hidden ${
                            isDark ? 'bg-[#1a1512] border-rock hover:border-[#00d4ff]/40' : 'bg-white border-gray-200 hover:border-[#00d4ff] shadow-sm'
                        } ${isHidden ? 'opacity-50 grayscale-[0.5]' : ''}`}>
                            <div className="flex flex-col lg:flex-row lg:items-center">
                                {/* Image & Basic Info */}
                                <div className="flex items-center gap-6 p-6 lg:w-2/5">
                                    <div className={`w-20 h-20 shrink-0 border overflow-hidden rounded-[10px] ${isDark ? 'border-rock bg-black' : 'border-gray-100 bg-gray-50'}`}>
                                        <img src={exp.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[8px] font-bold text-[#00d4ff] uppercase tracking-widest border border-[#00d4ff]/30 px-1.5 py-0.5 rounded-[10px]">{exp.category}</span>
                                            <span className={`text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-[10px] ${
                                                isHidden ? 'bg-gray-500/20 text-gray-500' :
                                                exp.id === 'horquetas' ? 'bg-[#00d4ff]/10 text-[#00d4ff]' : 'bg-[#25D366]/10 text-[#25D366]'
                                            }`}>
                                                {isHidden ? 'Oculta' : exp.id === 'horquetas' ? 'En Desarrollo' : 'Activa'}
                                            </span>
                                        </div>
                                        <h3 className={`text-lg font-title uppercase tracking-tight truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{exp.name_es}</h3>
                                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{exp.duration} • {exp.difficulty} • USD${exp.price_from}</p>
                                    </div>
                                </div>

                                {/* Stats/Details */}
                                <div className={`grid grid-cols-3 gap-8 px-6 py-4 border-y lg:border-y-0 lg:border-x transition-colors ${isDark ? 'border-rock' : 'border-gray-100'}`}>
                                    <div className="text-center">
                                        <p className="text-[8px] text-gray-600 font-bold uppercase tracking-[0.2em] mb-1">Vistas (30d)</p>
                                        <p className={`text-xl font-title ${isDark ? 'text-white' : 'text-gray-900'}`}>1.2k</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[8px] text-gray-600 font-bold uppercase tracking-[0.2em] mb-1">Reservas</p>
                                        <p className={`text-xl font-title ${isDark ? 'text-white' : 'text-gray-900'}`}>12</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[8px] text-gray-600 font-bold uppercase tracking-[0.2em] mb-1">Conversión</p>
                                        <p className="text-xl font-title text-[#00d4ff]">1.0%</p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-between lg:justify-end gap-3 p-6 lg:w-1/4">
                                    <button 
                                        onClick={() => handleToggleVisibility(exp.id)}
                                        className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-5 py-3 border transition-all rounded-[10px] ${
                                            isHidden 
                                            ? 'bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20' 
                                            : isDark ? 'bg-black border-rock text-gray-400 hover:text-white hover:border-white' : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-400'
                                        }`}
                                        title={isHidden ? 'Mostrar' : 'Ocultar'}
                                    >
                                        {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        <span className="text-[10px] font-bold uppercase tracking-widest lg:hidden">{isHidden ? 'Mostrar' : 'Ocultar'}</span>
                                    </button>
                                    <button 
                                        onClick={() => handleEditExpedition(exp)}
                                        className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-5 py-3 border transition-all rounded-[10px] ${
                                            isDark ? 'bg-black border-rock text-gray-400 hover:text-[#00d4ff] hover:border-[#00d4ff]/50' : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-[#00d4ff] hover:text-[#00d4ff]'
                                        }`}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest lg:hidden">Editar</span>
                                    </button>

                                    <div className={`h-10 w-px hidden lg:block mx-2 ${isDark ? 'bg-rock' : 'bg-gray-200'}`}></div>
                                    <button 
                                        onClick={() => handleDeleteClick(exp)}
                                        className={`p-3 border transition-all rounded-[10px] ${
                                            isDark ? 'bg-black border-rock text-gray-600 hover:text-red-500 hover:border-red-500/50' : 'bg-gray-50 border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-500'
                                        }`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {filteredExpeditions.length === 0 && (
                    <div className="text-center py-20 opacity-50 uppercase tracking-widest text-xs font-bold">
                        No se encontraron expediciones
                    </div>
                )}
            </div>

            {/* Translation Progress Overlay */}
            {isTranslating && createPortal(
                <div className="fixed inset-0 z-[300000] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className={`w-full max-w-xs p-8 rounded-[10px] border flex flex-col items-center text-center gap-5 shadow-[0_40px_100px_rgba(0,0,0,0.7)] ${
                        isDark ? 'bg-[#1a1512] border-rock text-white' : 'bg-white border-gray-100 text-gray-900'
                    }`}>
                        <div className="w-12 h-12 rounded-full border-4 border-[#00d4ff]/30 border-t-[#00d4ff] animate-spin" />
                        <div className="space-y-1">
                            <p className="text-xs font-black uppercase tracking-widest text-[#00d4ff]">Traduciendo</p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{translateProgress}</p>
                        </div>
                        <p className="text-[9px] text-gray-500 uppercase tracking-widest">EN · PT · FR · IT · DE</p>
                    </div>
                </div>,
                document.body
            )}

            {/* Notification Overlay */}
            {notification && (
                <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[200000] animate-in fade-in slide-in-from-top duration-500">
                    <div className={`px-8 py-5 flex items-center gap-4 shadow-2xl border-2 backdrop-blur-xl rounded-[10px] ${
                        notification.type === 'error' ? 'bg-red-600 border-red-400 text-white' : 'bg-[#25D366] border-[#25D366]/50 text-white'
                    }`}>
                        <div className="bg-white/20 p-2 rounded-full">
                            {notification.type === 'error' ? <AlertCircle className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
                        </div>
                        <div>
                            <p className="text-[12px] font-black uppercase tracking-widest">{notification.message}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Premium Confirmation Dialog */}
            {customConfirm && createPortal(
                <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className={`w-full max-w-sm p-8 shadow-[0_40px_100px_rgba(0,0,0,0.7)] rounded-[10px] border animate-in zoom-in-95 duration-300 ${
                        isDark ? 'bg-[#1a1512] border-rock text-white' : 'bg-white border-gray-100 text-gray-900'
                    }`}>
                        <div className="flex flex-col items-center text-center space-y-6">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-8 h-8 text-red-500" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-xl font-title uppercase tracking-tight">¿Confirmar acción?</h3>
                                <p className="text-[11px] opacity-70 font-bold uppercase tracking-widest leading-relaxed">
                                    {customConfirm.message}
                                </p>
                            </div>
                            <div className="flex flex-col w-full gap-3 pt-4">
                                <button 
                                    onClick={() => {
                                        customConfirm.onConfirm();
                                        setCustomConfirm(null);
                                    }}
                                    className="bg-red-500 text-white py-4 font-title text-sm uppercase tracking-widest hover:bg-red-600 transition-all rounded-[10px] shadow-lg shadow-red-500/20"
                                >
                                    Confirmar
                                </button>
                                <button 
                                    onClick={() => setCustomConfirm(null)}
                                    className={`py-4 text-[10px] font-black uppercase tracking-widest transition-all rounded-[10px] ${
                                        isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Expedition Editor Overlay */}
            {isEditorOpen && (
                <ExpeditionEditor 
                    theme={theme}
                    initialData={editingExp}
                    onClose={() => setIsEditorOpen(false)}
                    onSave={handleSave}
                    existingExpeditions={expeditions}
                />
            )}
        </div>
    );
};

export default ExpeditionsManager;
